import { useMemo } from 'react';

import styles from './TradingHoliday.module.css';
import SectionTitle from '@src/components/elements/SectionTitle';
import Holidays from './ui/Holidays';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/es';
import 'dayjs/locale/ar';
import { useStore } from '@lib/store';

dayjs.extend(localizedFormat);

const resolveDayjsLocale = (locale) => {
  if (!locale) return 'en';
  const normalized = locale.toLowerCase();
  if (normalized.includes('ar')) return 'ar';
  if (normalized === 'lat' || normalized.includes('es')) return 'es';
  return 'en';
};

const TradingHoliday = (props) => {
  const data = props.data || props;
  const storeLocale = useStore((state) => state.locale);
  const dayjsLocale = useMemo(() => resolveDayjsLocale(storeLocale), [storeLocale]);
  dayjs.locale(dayjsLocale);

  const tradingHolidayEntries = data?.trading_holidays_query?.data;
  const tradingHolidayRows = useMemo(() => {
    if (!Array.isArray(tradingHolidayEntries)) return [];
    return tradingHolidayEntries.flatMap((entry) => {
      if (!entry) return [];
      if (Array.isArray(entry?.json?.holidays)) return entry.json.holidays;
      if (Array.isArray(entry?.json)) return entry.json;
      if (Array.isArray(entry?.holidays)) return entry.holidays;
      return [];
    });
  }, [tradingHolidayEntries]);

  const formatMonthName = (monthIdx) =>
    Number.isInteger(monthIdx) ? dayjs().locale(dayjsLocale).month(monthIdx).format('MMMM') : '';

  const holidays = useMemo(() => {
    const rows = tradingHolidayRows;
    if (!Array.isArray(rows)) return [];

    const parseDate = (d) => {
      if (Object.prototype.toString.call(d) !== '[object String]') return { day: '', monthName: '', year: '' };
      const parts = d.split('.');
      if (parts.length !== 3) return { day: d, monthName: '', year: '' };
      const day = parts[0];
      const monthIdx = Number(parts[1]) - 1;
      const year = parts[2];
      return { day, monthName: formatMonthName(monthIdx), year };
    };

    const byYearMonth = new Map();
    rows.forEach((r, idx) => {
      const { monthName, year } = parseDate(r?.Date);
      const key = `${year}-${monthName}`;
      if (!byYearMonth.has(key)) byYearMonth.set(key, []);
      byYearMonth.get(key).push({ ...r, _idx: idx });
    });

    const calendarItems = [];
    let autoId = 0;
    byYearMonth.forEach((groupRows, key) => {
      const [year, monthName] = key.split('-');
      const eventsMap = new Map();
      groupRows.forEach((r) => {
        const dateStr = r?.Date || '';
        const evKey = dateStr;
        if (!eventsMap.has(evKey)) {
          eventsMap.set(evKey, {
            date: dateStr,
            dayOfTheWeek: '',
            name: r?.Holiday || '',
            tableRows: [],
          });
        }
        const event = eventsMap.get(evKey);
        const asset = r?.['Asset Class'] || r?.AssetClass || r?.assetClass || 'Asset';
        let row = event.tableRows.find((tr) => tr.name === asset);
        if (!row) {
          row = {
            name: asset,
            symbols: [],
            tradinghours: [],
          };
          event.tableRows.push(row);
        }
        const symbol = r?.Symbol || r?.symbol || '';
        if (symbol) row.symbols.push({ symbol });
        const status = r?.Status || r?.status || '';
        if (status) row.tradinghours.push({ state: status });
      });

      const id = autoId;
      autoId += 1;
      calendarItems.push({
        id,
        month: monthName,
        year,
        events: Array.from(eventsMap.values()),
      });
    });

    return calendarItems.sort((a, b) => a.id - b.id);
  }, [tradingHolidayRows, dayjsLocale]);

  if (!data) return null;

  return (
    <section className={`container ${styles.wrapper}`}>
      <div className={styles.header}>
        <SectionTitle data={data?.section_title} />
      </div>
      <div className={styles.surface}>
        <div className={styles.body}>
          {holidays?.length > 0 && <Holidays holidays={holidays} locale={dayjsLocale} />}
        </div>
      </div>
    </section>
  );
};

export default TradingHoliday;
